import { Alert, Button } from '@mui/material'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { generatePath, useParams } from 'react-router-dom'

import { StickyBox } from 'components/Box'
import { SaveButton, useCommonFormContextProps } from 'components/Form'
import { PageHeader } from 'components/Header'
import Loading from 'components/Loading'
import { DEFAULTS } from 'components/Paper/constants'
import { EndAbTestDialog } from 'features/ab-tests/components/EndAbTestDialog'
import { StatusChip } from 'features/ab-tests/components/StatusChip'
import { AbTestsAddEditContext } from 'features/ab-tests/contexts/AbTestsAddEditContext'
import { AbTestsEditForm } from 'features/ab-tests/forms/AbTestsEditForm'
import { useGetAbTest } from 'features/ab-tests/hooks/useGetAbTest'
import { cannotEditAbTest } from 'features/ab-tests/utils'
import { useAbility } from 'providers/casl'
import { ROUTES } from 'routes'
import { AbTestsEditUrlParams } from './types'

import type { AbTestsAddEditContextType } from 'features/ab-tests/contexts/AbTestsAddEditContext'

export const AbTestsEdit = (): JSX.Element => {
  const ability = useAbility()
  const { t } = useTranslation('features/abTests')

  const { id, workspaceId } = useParams<AbTestsEditUrlParams>()
  if (!id || !workspaceId) {
    throw Error('URL params are not valid')
  }

  const { abTest, loading } = useGetAbTest({ id })

  const formId: string = 'ab-tests-form-edit'
  const contextProps: AbTestsAddEditContextType = {
    ...useCommonFormContextProps({ formId }),
    abTest,
    isDisabled: cannotEditAbTest('edit', ability, abTest?.status),
  }

  const [isEndTestDialogOpen, setIsEndTestDialogOpen] = useState(false)

  if (!abTest || loading) {
    return <Loading />
  }

  const {
    domain: { activeAbTest },
    name,
    status,
  } = abTest

  const DraftStatusButtons = ({
    ctxProps,
  }: {
    ctxProps: AbTestsAddEditContextType
  }): JSX.Element => {
    const canUpdate: boolean = ability.can('update', 'AbTests')
    const canSaveAsDraft: boolean = canUpdate && status === 'draft'
    const canSaveAndStart: boolean = canUpdate && status === 'draft' && activeAbTest === undefined

    return (
      <>
        <SaveButton
          {...ctxProps}
          can={canSaveAsDraft}
          id={`${formId}-save-as-draft-button`}
          text={t('form.action.saveAsDraft')}
          variant='text'
        />
        <SaveButton
          {...ctxProps}
          can={canSaveAndStart}
          id={`${formId}-save-and-start-button`}
          text={t('form.action.saveAndStart')}
          tooltip={!canSaveAndStart ? { content: t('form.tooltip.activeTestOnDomain') } : undefined}
        />
      </>
    )
  }

  const ActiveStatusButton = (): JSX.Element => (
    <Button
      id={`${formId}-end-test-button`}
      onClick={(): void => setIsEndTestDialogOpen(true)}
      variant='contained'
    >
      {t('endTest.label')}
    </Button>
  )

  return (
    <AbTestsAddEditContext.Provider value={contextProps}>
      <StickyBox>
        <>
          <AbTestsAddEditContext.Consumer>
            {(ctxProps: AbTestsAddEditContextType): JSX.Element => (
              <PageHeader
                actions={
                  <>
                    {status === 'draft' && <DraftStatusButtons ctxProps={ctxProps} />}
                    {status === 'active' && <ActiveStatusButton />}
                  </>
                }
                backTo={generatePath(ROUTES.AB_TESTS.LIST, { workspaceId })}
                title={name}
                titleAdornment={{
                  end: <StatusChip status={status} />,
                }}
              />
            )}
          </AbTestsAddEditContext.Consumer>
        </>
      </StickyBox>

      {abTest?.status === 'active' && (
        <>
          <Alert
            severity='warning'
            sx={{
              color: theme => theme.palette.white.main,
              m: DEFAULTS.MARGIN,
              md: 10,
            }}
            variant='filled'
          >
            {t('common.header.activeTestAlert')}
          </Alert>

          <EndAbTestDialog
            abTest={{
              domain: { name: abTest.domain.name },
              id,
              usedVariantsData: abTest.usedVariantsData,
            }}
            isOpen={isEndTestDialogOpen}
            setOpen={setIsEndTestDialogOpen}
          />
        </>
      )}

      <AbTestsEditForm />
    </AbTestsAddEditContext.Provider>
  )
}
